import React, { useState, useEffect } from 'react'
import { getPermissoes, updatePermissoes } from '../../lib/supabase'
import { PAGINAS_PERMISSAO, ACOES_PERMISSAO, ESTADOS_AGENDA_PERMISSAO, ESTADOS_RELATORIO_PERMISSAO, ESTADOS_FATURACAO_PERMISSAO, PAPEIS_CONFIG } from '../../lib/permissoes'
import { useTenant } from '../../contexts/TenantContext'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import Loading from '../../components/Loading'

const thStyle = { padding: '12px 10px', fontSize: '12px', fontWeight: '600', color: 'var(--cor-texto-suave)', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'center' }
const tdCentro = { padding: '12px 10px', textAlign: 'center' }

// Tabela genérica de permissões (página, ação ou estado) × papel.
// `colLabel` = título da 1ª coluna. Se um item tiver `cor`, o ícone é
// pintado nessa cor (legenda de cor) e mostra-se a `descricao` por baixo.
function TabelaPermissoes({ titulo, descricao, colLabel, itens, mapa, onToggle }) {
  return (
    <div style={{ marginBottom: '28px' }}>
      <p style={{ fontWeight: '600', marginBottom: '4px', fontSize: '15px' }}>{titulo}</p>
      <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '12px', fontSize: '13px' }}>{descricao}</p>
      <div style={{
        background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
        borderRadius: 'var(--raio-lg)', overflow: 'hidden', boxShadow: 'var(--sombra)',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--cor-creme)', borderBottom: '1px solid var(--cor-borda)' }}>
              <th style={{ ...thStyle, textAlign: 'left' }}>{colLabel}</th>
              {PAPEIS_CONFIG.map(p => <th key={p.chave} style={thStyle}>{p.label}</th>)}
              <th style={thStyle}>Super Admin</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item, i) => (
              <tr key={item.chave} style={{ borderBottom: i < itens.length - 1 ? '1px solid var(--cor-borda)' : 'none' }}>
                <td style={{ padding: '12px 14px', fontSize: '14px' }}>
                  <span aria-hidden="true" style={{ marginRight: '8px', color: item.cor || 'inherit' }}>{item.icon}</span>
                  {item.label}
                  {item.descricao && (
                    <div style={{ color: 'var(--cor-texto-suave)', fontSize: '12px', marginTop: '2px', marginLeft: '24px' }}>
                      {item.descricao}
                    </div>
                  )}
                </td>
                {PAPEIS_CONFIG.map(papel => (
                  <td key={papel.chave} style={tdCentro}>
                    <input
                      type="checkbox"
                      checked={!!mapa[item.chave]?.[papel.chave]}
                      onChange={() => onToggle(item.chave, papel.chave)}
                      aria-label={`${item.label} — ${papel.label}`}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--cor-principal)' }}
                    />
                  </td>
                ))}
                <td style={tdCentro}>
                  <span title="O Super Admin tem sempre acesso" style={{ color: 'var(--cor-texto-suave)' }}>✓</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function Permissoes() {
  const { tenant } = useTenant()
  const { recarregarPermissoes } = useAuth()
  const toast = useToast()
  const [perm, setPerm] = useState(null) // { paginas, acoes }
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    if (!tenant) return
    getPermissoes(tenant.id)
      .then(setPerm)
      .catch(e => toast.erro(e.message))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant])

  function toggle(grupo, chave, papel) {
    setPerm(prev => ({
      ...prev,
      [grupo]: { ...prev[grupo], [chave]: { ...prev[grupo][chave], [papel]: !prev[grupo][chave][papel] } },
    }))
  }

  async function guardar() {
    setGuardando(true)
    try {
      await updatePermissoes(tenant.id, perm)
      await recarregarPermissoes()
      toast.sucesso('Permissões guardadas com sucesso.')
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setGuardando(false)
    }
  }

  if (!perm) return <div style={{ padding: '64px 24px' }}><Loading /></div>

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '32px 24px 64px' }}>
      <h2 style={{ marginBottom: '6px', fontSize: '22px' }}>Permissões</h2>
      <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '24px', fontSize: '14px' }}>
        Define o que cada papel pode fazer no backoffice. O <strong>Super Admin</strong> tem sempre acesso a tudo.
      </p>

      <TabelaPermissoes
        titulo="Páginas"
        descricao="A que páginas do backoffice cada papel pode aceder."
        colLabel="Página"
        itens={PAGINAS_PERMISSAO}
        mapa={perm.paginas}
        onToggle={(chave, papel) => toggle('paginas', chave, papel)}
      />

      <TabelaPermissoes
        titulo="Ações"
        descricao="Funcionalidades específicas dentro das páginas."
        colLabel="Ação"
        itens={ACOES_PERMISSAO}
        mapa={perm.acoes}
        onToggle={(chave, papel) => toggle('acoes', chave, papel)}
      />

      <TabelaPermissoes
        titulo="Estados visíveis na Agenda"
        descricao="Que estados de marcação cada papel vê na Agenda do Dia e do Mês. Estados não permitidos são ocultados."
        colLabel="Estado"
        itens={ESTADOS_AGENDA_PERMISSAO}
        mapa={perm.estadosAgenda}
        onToggle={(chave, papel) => toggle('estadosAgenda', chave, papel)}
      />

      <TabelaPermissoes
        titulo="Estados filtráveis no Relatório"
        descricao="Que estados de marcação cada papel pode filtrar e ver no Relatório."
        colLabel="Estado"
        itens={ESTADOS_RELATORIO_PERMISSAO}
        mapa={perm.estadosRelatorio}
        onToggle={(chave, papel) => toggle('estadosRelatorio', chave, papel)}
      />

      <TabelaPermissoes
        titulo="Estados visíveis na Faturação"
        descricao="Que estados de marcação cada papel vê no relatório de Faturação. Uma fatura só aparece se alguma das suas marcações estiver num estado permitido; no detalhe, as linhas dos estados não permitidos ficam ocultas."
        colLabel="Estado"
        itens={ESTADOS_FATURACAO_PERMISSAO}
        mapa={perm.estadosFaturacao}
        onToggle={(chave, papel) => toggle('estadosFaturacao', chave, papel)}
      />

      <button
        onClick={guardar}
        className="btn-primario"
        disabled={guardando}
        style={{ opacity: guardando ? 0.6 : 1, padding: '10px 24px', fontSize: '14px' }}
      >
        {guardando ? 'A guardar…' : 'Guardar permissões'}
      </button>

      <p style={{ color: 'var(--cor-texto-suave)', fontSize: '12px', marginTop: '16px', lineHeight: 1.6 }}>
        Nota: isto controla o acesso às páginas e ações do backoffice. A proteção dos dados na base de dados
        (RLS) mantém-se como camada de segurança.
      </p>
    </div>
  )
}
